import React from "react";
import { FormattedMessage } from "react-intl";
import { ExternalLink } from "../../components/ExternalLink";
import {
  AUGMENTT_POSTURE_STATES,
  COMPLIANCE_TYPES,
  EXTERNAL_MAIL_FORWARDING_OPTIONS,
  SECURE_CHECK_TYPES,
  SHORTCUTS,
} from "./constants";

import { secureScoreProfilePostureConfigs } from "./SecureScoreProfile";

const postureConfig = {
  [SECURE_CHECK_TYPES.ADMIN_MFA]: {
    shortcut: SHORTCUTS.CONFIGURE,
    rationale: "SECURE.ADMIN_MFA_M365_DETAILS",
    compliance: {
      [COMPLIANCE_TYPES.CIS_31]: ["5.1.1.1", "5.1.2.1", "5.2.2.1", "5.2.3.4"],
      [COMPLIANCE_TYPES["NIST_CSF_2.0"]]: ["PR.AA-02"],
      [COMPLIANCE_TYPES.CISA_SCUBA]: ["MS.AAD.3.1", "MS.AAD.3.2"],
      [COMPLIANCE_TYPES.ESSENTIAL_8_L1]: ["1504", "1679", "1680", "1681"],
      [COMPLIANCE_TYPES.ESSENTIAL_8_L2]: [
        "1504",
        "1679",
        "1680",
        "1681",
        "1173",
        "1401",
      ],
      [COMPLIANCE_TYPES.ESSENTIAL_8_L3]: [
        "1504",
        "1679",
        "1680",
        "1681",
        "1173",
        "1401",
        "1892",
        "1893",
        "1505",
      ],
    },

    statusDetailsFormatter: (row) => (
      <FormattedMessage
        id="SECURE.ADMIN_MFA_M365_HEADER"
        values={{
          count: row.compliance.nonCompliant,
          total: row.compliance.total,
        }}
      />
    ),
  },
  [SECURE_CHECK_TYPES.USER_MFA]: {
    shortcut: SHORTCUTS.CONFIGURE,
    rationale: "SECURE.USER_MFA_M365_DETAILS",
    compliance: {
      [COMPLIANCE_TYPES.CIS_31]: ["5.1.1.1", "5.1.2.1", "5.2.2.2", "5.2.3.4"],
      [COMPLIANCE_TYPES["NIST_CSF_2.0"]]: ["PR.AA-02"],
      [COMPLIANCE_TYPES.CISA_SCUBA]: ["MS.AAD.3.1", "MS.AAD.3.2"],
      [COMPLIANCE_TYPES.ESSENTIAL_8_L1]: ["1504", "1679", "1680", "1681"],
      [COMPLIANCE_TYPES.ESSENTIAL_8_L2]: [
        "1504",
        "1679",
        "1680",
        "1681",
        "1401",
      ],
      [COMPLIANCE_TYPES.ESSENTIAL_8_L3]: [
        "1504",
        "1679",
        "1680",
        "1681",
        "1401",
      ],
    },

    statusDetailsFormatter: (row) => (
      <FormattedMessage
        id="SECURE.USER_MFA_M365_HEADER"
        values={{
          count: row.compliance.nonCompliant,
          total: row.compliance.total,
        }}
      />
    ),
  },
  [SECURE_CHECK_TYPES.RISKY_IPS]: {
    shortcut: [SHORTCUTS.INSTRUCTIONS, SHORTCUTS.CAP],
    rationale: "SECURE.RISKY_IP_ADDRESS_POLICY_DETAIL",
    statusDetailsFormatter: (row) => (
      <FormattedMessage
        id="SECURE.RISKY_IP_ADDRESS_POLICY_HEADER"
        values={{ value: row.compliance.compliant ? "true" : "false" }}
      />
    ),
  },
  [SECURE_CHECK_TYPES.INACTIVE_ACCOUNTS]: {
    shortcut: SHORTCUTS.INSTRUCTIONS,
    rationale: "SECURE.INACTIVE_ACCOUNTS_DETAIL",
    compliance: {
      [COMPLIANCE_TYPES["NIST_CSF_2.0"]]: ["PR.AA-01"],
      [COMPLIANCE_TYPES.ESSENTIAL_8_L1]: ["1648"],
      [COMPLIANCE_TYPES.ESSENTIAL_8_L2]: ["1648"],
      [COMPLIANCE_TYPES.ESSENTIAL_8_L3]: ["1648"],
    },
    statusDetailsFormatter: (row) => (
      <FormattedMessage
        id="SECURE.INACTIVE_ACCOUNTS_HEADER"
        values={{
          count: row.compliance.nonCompliant,
          total: row.compliance.total,
        }}
      />
    ),
  },
  [SECURE_CHECK_TYPES.LOGIN_PORTAL_BRANDING]: {
    shortcut: SHORTCUTS.INSTRUCTIONS,
    rationale: "SECURE.LOGIN_PORTAL_BRANDING_M365_DETAILS",
    statusDetailsFormatter: (row) => (
      <FormattedMessage
        id="SECURE.LOGIN_PORTAL_BRANDING_M365_HEADER"
        values={{ value: row.compliance.compliant ? "true" : "false" }}
      />
    ),
  },
  [SECURE_CHECK_TYPES.UNIFIED_AUDIT_LOG]: {
    shortcut: SHORTCUTS.CONFIGURE,
    rationale: "SECURE.UNIFIED_AUDIT_LOG_DETAILS",
    compliance: {
      [COMPLIANCE_TYPES.CIS_31]: ["3.1.1"],
      [COMPLIANCE_TYPES["NIST_CSF_2.0"]]: ["PR.PS-04"],
      [COMPLIANCE_TYPES.CISA_SCUBA]: ["MS.EXO.17.1", "MS.EXO.17.2"],
      [COMPLIANCE_TYPES.ESSENTIAL_8_L2]: ["1683", "1509", "1650"],
      [COMPLIANCE_TYPES.ESSENTIAL_8_L3]: ["1683", "1509", "1650"],
    },

    statusDetailsFormatter: (row) => (
      <FormattedMessage
        id="SECURE.PASSWORD_RESET_M365_HEADER"
        values={{ value: row.compliance.compliant ? "true" : "false" }}
      />
    ),
  },
  [SECURE_CHECK_TYPES.SAFE_ATTACHMENT]: {
    shortcut: SHORTCUTS.CONFIGURE,
    rationale: "SECURE.SAFE_ATTACHMENTS_DETAILS",
    compliance: {
      [COMPLIANCE_TYPES.CIS_31]: ["2.1.5"],
      [COMPLIANCE_TYPES["NIST_CSF_2.0"]]: ["DE.CM-09"],
      [COMPLIANCE_TYPES.CISA_SCUBA]: ["MS.DEFENDER.3.1"],
    },

    statusDetailsFormatter: (row) => (
      <FormattedMessage
        id="SECURE.SAFE_ATTACHMENTS_HEADER"
        values={{
          count: row.compliance.total - row.compliance.nonCompliant,
          total: row.compliance.total,
        }}
      />
    ),
  },
  [SECURE_CHECK_TYPES.EXTERNAL_MAIL_FORWARDING]: {
    shortcut: SHORTCUTS.CONFIGURE,
    rationale: "SECURE.ONE_DRIVE_EXTERNAL_MAIL_FORWARDING_DETAILS",
    compliance: {
      [COMPLIANCE_TYPES.CIS_31]: ["6.2.1"],
      [COMPLIANCE_TYPES["NIST_CSF_2.0"]]: ["PR.PS-01"],
      [COMPLIANCE_TYPES.CISA_SCUBA]: ["MS.DEFENDER.1.1", "MS.DEFENDER.1.2"],
    },

    statusDetailsFormatter: (row) => (
      <FormattedMessage
        id="SECURE.ONE_DRIVE_EXTERNAL_MAIL_FORWARDING_HEADER"
        values={{
          value: EXTERNAL_MAIL_FORWARDING_OPTIONS.find(
            (option) => option.value === row.compliance.compliant
          )?.label,
        }}
      />
    ),
  },
  [SECURE_CHECK_TYPES.RISKY_COUNTRIES]: {
    shortcut: [SHORTCUTS.INSTRUCTIONS, SHORTCUTS.CAP],
    rationale: "SECURE.RISKY_COUNTRY_POLICY_DETAIL",
    statusDetailsFormatter: (row) => (
      <FormattedMessage
        id="SECURE.RISKY_COUNTRY_POLICY_HEADER"
        values={{ value: row.compliance.compliant ? "true" : "false" }}
      />
    ),
  },
  [SECURE_CHECK_TYPES.BLOCK_LEGACY_AUTHENTICATION]: {
    shortcut: [SHORTCUTS.INSTRUCTIONS, SHORTCUTS.CAP],
    rationale: "SECURE.BLOCK_LEGACY_AUTHENTICATION",
    compliance: {
      [COMPLIANCE_TYPES.CIS_31]: ["5.2.2.3"],
      [COMPLIANCE_TYPES["NIST_CSF_2.0"]]: ["PR.AA-02", "PR.AA-01"],
      [COMPLIANCE_TYPES.CISA_SCUBA]: ["MS.AAD.1.1"],
    },
    statusDetailsFormatter: (row) => (
      <FormattedMessage
        id="SECURE.BLOCK_LEGACY_AUTHENTICATION_HEADER"
        values={{
          count: row.compliance.total - row.compliance.nonCompliant,
          total: row.compliance.total,
        }}
      />
    ),
  },
  [SECURE_CHECK_TYPES.SELF_SERVE_PASSWORD_RESET]: {
    shortcut: SHORTCUTS.INSTRUCTIONS,
    rationale: "SECURE.SELF_SERVICE_PASSWORD_M365_DETAILS",
    compliance: { [COMPLIANCE_TYPES.CIS_31]: ["5.2.4.1"] },
    statusDetailsFormatter: (row) => (
      <FormattedMessage
        id="SECURE.SELF_SERVICE_PASSWORD_M365_HEADER"
        values={{
          count: row.compliance.nonCompliant,
          total: row.compliance.total,
        }}
      />
    ),
  },
  [SECURE_CHECK_TYPES.TEAMS_DEFAULT_EXTERNAL_OPTIONS]: {
    shortcut: null,
    rationale: "SECURE.TEAMS_EXTERNAL_GUEST_SHARING_DETAILS",
    compliance: {
      [COMPLIANCE_TYPES.CIS_20]: ["3.3"],
      [COMPLIANCE_TYPES["NIST_CSF_2.0"]]: ["PR.IR-01"],
      [COMPLIANCE_TYPES.CISA_SCUBA]: ["MS.TEAMS.3.1"],
    },

    statusDetailsFormatter: (row) => (
      <FormattedMessage
        id="SECURE.TEAMS_EXTERNAL_GUEST_SHARING_HEADER"
        values={{
          count: row.compliance.total - row.compliance.nonCompliant,
          total: row.compliance.total,
        }}
      />
    ),
  },
  [SECURE_CHECK_TYPES.SHAREPOINT_EXTERNAL_GUEST_SHARING]: {
    shortcut: null,
    rationale: "SECURE.SHARE_POINT_EXTERNAL_GUEST_SHARING_DETAILS",
    compliance: {
      [COMPLIANCE_TYPES.CIS_31]: ["7.2.3"],
      [COMPLIANCE_TYPES["NIST_CSF_2.0"]]: ["PR.DS-02"],
      [COMPLIANCE_TYPES.CISA_SCUBA]: ["MS.AAD.8.3"],
    },

    statusDetailsFormatter: (row) => (
      <FormattedMessage
        id="SECURE.SHARE_POINT_EXTERNAL_GUEST_SHARING_HEADER"
        values={{
          count: row.compliance.total - row.compliance.nonCompliant,
          total: row.compliance.total,
        }}
      />
    ),
  },
  [SECURE_CHECK_TYPES.CONNECTED_APPS_ADMIN_CONSENT]: {
    shortcut: SHORTCUTS.CONFIGURE,
    rationale: "SECURE.CONNECTED_APPS_ADMIN_CONSENT_HEADER_DETAILS",
    compliance: {
      [COMPLIANCE_TYPES.CIS_31]: ["5.1.5.2"],
      [COMPLIANCE_TYPES["NIST_CSF_2.0"]]: ["ID.RA-09", "PR.IR-01"],
      [COMPLIANCE_TYPES.CISA_SCUBA]: ["MS.AAD.5.2"],
    },
    statusDetailsFormatter: (row) => (
      <FormattedMessage
        id="SECURE.CONNECTED_APPS_ADMIN_CONSENT_HEADER"
        values={{
          count: row.compliance.connectedAppsAndAdminConsent,
          action: !row.status?.toLowerCase().includes("not")
            ? "may not"
            : "may",
        }}
      />
    ),
  },
  [SECURE_CHECK_TYPES.BLOCK_SIGN_IN_SHARED_MAILBOXES]: {
    shortcut: SHORTCUTS.CONFIGURE,
    rationale: "SECURE.BLOCK_SIGN_IN_SHARED_MAILBOX_DETAIL",
    compliance: {
      [COMPLIANCE_TYPES.CIS_31]: ["1.2.2"],
    },
    statusDetailsFormatter: (row) => (
      <FormattedMessage
        id="SECURE.BLOCK_SIGN_IN_SHARED_MAILBOX_HEADER"
        values={{
          count: row.compliance.compliant,
          total: row.compliance.total,
          action: !row.status?.toLowerCase().includes("not")
            ? "may not"
            : "may",
        }}
      />
    ),
  },
  [SECURE_CHECK_TYPES.MAILBOX_AUDIT]: {
    shortcut: SHORTCUTS.CONFIGURE,
    rationale: "SECURE.MAILBOX_AUDIT_DETAILS",
    compliance: {
      [COMPLIANCE_TYPES.CIS_31]: ["6.1.2", "6.1.3"],
      [COMPLIANCE_TYPES["NIST_CSF_2.0"]]: ["PR.PS-04"],
      [COMPLIANCE_TYPES.CISA_SCUBA]: ["MS.EXO.13.1"],
      [COMPLIANCE_TYPES.ESSENTIAL_8_L2]: ["1683"],
      [COMPLIANCE_TYPES.ESSENTIAL_8_L3]: ["1683"],
    },

    statusDetailsFormatter: (row) => (
      <FormattedMessage
        id="SECURE.MAILBOX_AUDIT_HEADER"
        values={{
          status:
            row.compliance.compliant > 0 ? (
              <FormattedMessage id="UIC.GENERAL.TRUE" />
            ) : (
              <FormattedMessage id="UIC.GENERAL.FALSE" />
            ),
          action: !row.status?.toLowerCase().includes("not")
            ? "may not"
            : "may",
        }}
      />
    ),
  },
  [SECURE_CHECK_TYPES.EXTERNAL_MAIL_TAG]: {
    shortcut: SHORTCUTS.CONFIGURE,
    rationale: "SECURE.EXTERNAIL_MAIL_TAG_DESCRIPTION",
    compliance: {
      [COMPLIANCE_TYPES.CIS_31]: ["6.2.3"],
      [COMPLIANCE_TYPES["NIST_CSF_2.0"]]: ["DE.CM-03"],
      [COMPLIANCE_TYPES.CISA_SCUBA]: ["MS.EXO.7.1"],
    },
    statusDetailsFormatter: (row) => (
      <FormattedMessage
        id="SECURE.EXTERNAIL_MAIL_TAG_HEADER"
        values={{
          status:
            row.compliance.nonCompliant > 0 ? (
              <FormattedMessage id="UIC.GENERAL.NOT" />
            ) : (
              ""
            ),
          action: !row.status?.toLowerCase().includes("not")
            ? "may not"
            : "may",
        }}
      />
    ),
  },
  [SECURE_CHECK_TYPES.POWERSHELL_ACCESS_FOR_NON_ADMINS]: {
    shortcut: SHORTCUTS.CONFIGURE,
    rationale: "SECURE.POWERSHELL_ACCESS_FOR_NONADMINS_DETAIL",
    compliance: {
      [COMPLIANCE_TYPES.ESSENTIAL_8_L1]: ["1688"],
      [COMPLIANCE_TYPES.ESSENTIAL_8_L2]: ["1688"],
      [COMPLIANCE_TYPES.ESSENTIAL_8_L3]: ["1688"],
    },
    statusDetailsFormatter: (row) => (
      <FormattedMessage
        id="SECURE.POWERSHELL_ACCESS_FOR_NONADMINS_HEADER"
        values={{
          count: row.compliance.compliant,
          total: row.compliance.total,
          action: !row.status?.toLowerCase().includes("not")
            ? "may not"
            : "may",
        }}
      />
    ),
  },
  [SECURE_CHECK_TYPES.DKIM]: {
    shortcut: SHORTCUTS.CONFIGURE,
    rationale: "SECURE.DKIM",
    compliance: {
      [COMPLIANCE_TYPES.CIS_31]: ["2.1.9"],
      [COMPLIANCE_TYPES["NIST_CSF_2.0"]]: ["PR.PS-01"],
      [COMPLIANCE_TYPES.CISA_SCUBA]: ["MS.EXO.3.1"],
    },
    statusDetailsFormatter: (row) => (
      <FormattedMessage
        id="SECURE.POSTURE.NUM_SETTINGS_APPLIED"
        values={{
          count: row?.compliance?.compliant,
          total: row?.compliance?.total,
        }}
      />
    ),
  },
  [SECURE_CHECK_TYPES.CONTACT_SETTINGS]: {
    shortcut: SHORTCUTS.CONFIGURE,
    rationale: "SECURE.CONTACT_SETTINGS_DETAILS",
    statusDetailsFormatter: (row) => (
      <FormattedMessage
        id="SECURE.POSTURE.NUM_SETTINGS_APPLIED"
        values={{
          count: row?.compliance?.compliant,
          total: row?.compliance?.total,
        }}
      />
    ),
  },
  [SECURE_CHECK_TYPES.RESHARING_BY_EXTERNAL_USERS]: {
    shortcut: SHORTCUTS.CONFIGURE,
    rationale: "SECURE.RESHARING_BY_EXTERNAL_USERS_DETAILS",
    compliance: { [COMPLIANCE_TYPES["NIST_CSF_2.0"]]: ["PR.DS-02"] },
    statusDetailsFormatter: (row) => (
      <FormattedMessage
        id="SECURE.RESHARING_BY_EXTERNAL_USERS_HEADER"
        values={{
          status:
            row.compliance.nonCompliant > 0 ? (
              <FormattedMessage id="UIC.GENERAL.NOT" />
            ) : (
              ""
            ),
        }}
      />
    ),
  },
  [SECURE_CHECK_TYPES.SITE_CREATION_FOR_STANDARD_USERS]: {
    shortcut: SHORTCUTS.CONFIGURE,
    rationale: "SECURE.SITE_CREATION_BY_STANDARD_USERS_DETAILS",
    statusDetailsFormatter: (row) => (
      <FormattedMessage
        id="SECURE.SITE_CREATION_BYSTANDARD_USERS_HEADER"
        values={{
          status:
            row.compliance.nonCompliant > 0 ? (
              <FormattedMessage id="UIC.GENERAL.NOT" />
            ) : (
              ""
            ),
        }}
      />
    ),
  },
  [SECURE_CHECK_TYPES.DELETED_USER_ONE_DRIVE_RETENTION]: {
    shortcut: SHORTCUTS.CONFIGURE,
    rationale: (
      <>
        <FormattedMessage id="SECURE.RETAIN_DELETED_USER_ONAEDRIVE_FOR_ONE_YEAR_DETAILS" />
        <div>
          <ExternalLink
            href="https://learn.microsoft.com/en-us/sharepoint/unlicensed-onedrive-accounts"
            label="Beginning January 27, 2025, Microsoft will start charging for storage used by unlicensed accounts, click here for more details"
          />
        </div>
      </>
    ),
    compliance: { [COMPLIANCE_TYPES["NIST_CSF_2.0"]]: ["PR.DS-09"] },
    statusDetailsFormatter: (row) => (
      <FormattedMessage
        id="SECURE.RETAIN_DELETED_USER_ONAEDRIVE_FOR_ONE_YEAR_HEADER"
        values={{
          status:
            row.compliance.nonCompliant > 0 ? (
              <FormattedMessage id="UIC.GENERAL.NOT" />
            ) : (
              ""
            ),
        }}
      />
    ),
  },
  [SECURE_CHECK_TYPES.MFA_NUMBER_MATCHING]: {
    shortcut: SHORTCUTS.CONFIGURE,
    rationale: "SECURE.MFA_NUMBER_MATCHING",
    compliance: { [COMPLIANCE_TYPES.CIS_31]: ["5.2.3.1"] },
    statusDetailsFormatter: (row) => (
      <FormattedMessage
        id="SECURE.POSTURE.NUM_SETTINGS_APPLIED"
        values={{
          count: row?.compliance?.compliant,
          total: row?.compliance?.total,
        }}
      />
    ),
  },
  [SECURE_CHECK_TYPES.ENABLE_EXCHANGE_MODERN_AUTH]: {
    shortcut: SHORTCUTS.CONFIGURE,
    rationale: "SECURE.ENABLE_EXCHANGE_MODERN_AUTH_DETAILS",
    compliance: { [COMPLIANCE_TYPES.CIS_31]: ["6.5.1"] },
    statusDetailsFormatter: (row) => (
      <FormattedMessage
        id="SECURE.ENABLE_EXCHANGE_MODERN_AUTH_HEADER"
        values={{
          status:
            row.compliance.nonCompliant > 0 ? (
              <FormattedMessage id="UIC.GENERAL.NOT" />
            ) : (
              ""
            ),
        }}
      />
    ),
  },
  [SECURE_CHECK_TYPES.ENTRA_ID_GUEST_USER_ACCESS_PERMISSIONS]: {
    shortcut: SHORTCUTS.CONFIGURE,
    rationale: "SECURE.ENTRA_ID_GUEST_USER_ACCESS_PERMISSION_DETAILS",
    compliance: {
      [COMPLIANCE_TYPES.CIS_31]: ["5.1.2.4"],
      [COMPLIANCE_TYPES["NIST_CSF_2.0"]]: ["PR.AA-05"],
      [COMPLIANCE_TYPES.CISA_SCUBA]: ["MS.AAD.8.1", "MS.AAD.8.2"],
      [COMPLIANCE_TYPES.ESSENTIAL_8_L1]: ["1688"],
      [COMPLIANCE_TYPES.ESSENTIAL_8_L2]: ["1688"],
      [COMPLIANCE_TYPES.ESSENTIAL_8_L3]: ["1688"],
    },
    statusDetailsFormatter: (row) => (
      <FormattedMessage
        id="SECURE.ENTRA_ID_GUEST_USER_ACCESS_PERMISSION_HEADER"
        values={{
          status:
            row.compliance.nonCompliant > 0 ? (
              <FormattedMessage id="UIC.GENERAL.NOT" />
            ) : (
              ""
            ),
        }}
      />
    ),
  },
  [SECURE_CHECK_TYPES.TEMPORARY_ACCESS_PASS]: {
    shortcut: SHORTCUTS.CONFIGURE,
    rationale: "SECURE.TEMPORARY_ACCESS_PASS",
    statusDetailsFormatter: (row) => (
      <FormattedMessage
        id="SECURE.POSTURE.NUM_SETTINGS_APPLIED"
        values={{
          count: row?.compliance?.compliant,
          total: row?.compliance?.total,
        }}
      />
    ),
  },
  [SECURE_CHECK_TYPES.MFA_CAMPAIGN]: {
    shortcut: SHORTCUTS.CONFIGURE,
    rationale: (
      <>
        <FormattedMessage id="SECURE.MFA_CAMPAIGN" />
        &nbsp;
        <ExternalLink
          href="https://learn.microsoft.com/en-us/azure/active-directory/authentication/how-to-mfa-registration-campaign"
          label={<FormattedMessage id="SECURE.MFA_CAMPAIGN_LINK" />}
        />
      </>
    ),
    statusDetailsFormatter: () => (
      <FormattedMessage id="SECURE.MFA_CAMPAIGN_HEADER" />
    ),
  },
  [SECURE_CHECK_TYPES.ACTIVITY_BASED_TIMEOUT_POLICY]: {
    shortcut: SHORTCUTS.CONFIGURE,
    rationale: "SECURE.IDLE_SESSION_TIMEOUT_DETAILS",
    compliance: { [COMPLIANCE_TYPES.CIS_31]: ["1.3.2"] },
    statusDetailsFormatter: (row) => (
      <FormattedMessage
        id="SECURE.IDLE_SESSION_TIMEOUT_HEADER"
        values={{
          status:
            row.compliance.nonCompliant > 0 ? (
              <FormattedMessage id="UIC.GENERAL.NOT" />
            ) : (
              ""
            ),
        }}
      />
    ),
  },
  // MAESTER POSTURES
  [SECURE_CHECK_TYPES.MT_1031]: {
    rationale: "SECURE.RATIONALE.MT_1031",
    compliance: {
      [COMPLIANCE_TYPES.ESSENTIAL_8_L1]: ["1507"],
      [COMPLIANCE_TYPES.ESSENTIAL_8_L2]: ["1507"],
      [COMPLIANCE_TYPES.ESSENTIAL_8_L3]: ["1507", "1649"],
    },
    statusDetailsFormatter: (row) =>
      row.status === AUGMENTT_POSTURE_STATES.COMPLIANT ? (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.POSITIVE.MT_1031" />
      ) : (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.NEGATIVE.MT_1031" />
      ),
  },
  [SECURE_CHECK_TYPES.MT_1030]: {
    rationale: "SECURE.RATIONALE.MT_1030",
    compliance: {
      [COMPLIANCE_TYPES.ESSENTIAL_8_L2]: ["1648"],
      [COMPLIANCE_TYPES.ESSENTIAL_8_L3]: ["1648"],
    },
    statusDetailsFormatter: (row) =>
      row.status === AUGMENTT_POSTURE_STATES.COMPLIANT ? (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.POSITIVE.MT_1030" />
      ) : (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.NEGATIVE.MT_1030" />
      ),
  },
  [SECURE_CHECK_TYPES.MT_1026]: {
    rationale: (
      <>
        <FormattedMessage id="SECURE.RATIONALE.MT_1026" />
        <div>
          <ExternalLink
            href="https://learn.microsoft.com/en-us/entra/identity/role-based-access-control/security-planning#ensure-separate-user-accounts-and-mail-forwarding-for-global-administrator-accounts"
            label="https://learn.microsoft.com/en-us/entra/identity/role-based-access-control/security-planning#ensure-separate-user-accounts-and-mail-forwarding-for-global-administrator-accounts"
          />
        </div>
      </>
    ),
    statusDetailsFormatter: () => (
      <FormattedMessage id="SECURE.STATUS_DESCRIPTION.MT_1026" />
    ),
    compliance: {
      [COMPLIANCE_TYPES.CIS_31]: ["1.1.1"],
    },
  },
  [SECURE_CHECK_TYPES.MT_1020]: {
    rationale: (
      <>
        <FormattedMessage id="SECURE.RATIONALE.MT_1020" />
        <div>
          <ExternalLink
            href="https://learn.microsoft.com/en-us/entra/identity/conditional-access/howto-conditional-access-policy-admin-mfa"
            label="https://learn.microsoft.com/en-us/entra/identity/conditional-access/howto-conditional-access-policy-admin-mfa"
          />
        </div>
      </>
    ),
    statusDetailsFormatter: (row) =>
      row.status === AUGMENTT_POSTURE_STATES.COMPLIANT ? (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.POSITIVE.MT_1020" />
      ) : (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.NEGATIVE.MT_1020" />
      ),
  },
  [SECURE_CHECK_TYPES.MT_1019]: {
    shortcut: SHORTCUTS.CAP,
    rationale: (
      <>
        <FormattedMessage id="SECURE.RATIONALE.MT_1019" />
        <div>
          <ExternalLink
            href="https://learn.microsoft.com/en-us/entra/identity/conditional-access/howto-policy-app-enforced-restriction"
            label="https://learn.microsoft.com/en-us/entra/identity/conditional-access/howto-policy-app-enforced-restriction"
          />
        </div>
      </>
    ),
    statusDetailsFormatter: (row) =>
      row.status === AUGMENTT_POSTURE_STATES.COMPLIANT ? (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.POSITIVE.MT_1019" />
      ) : (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.NEGATIVE.MT_1019" />
      ),
  },
  [SECURE_CHECK_TYPES.MT_1018]: {
    rationale: (
      <>
        <FormattedMessage id="SECURE.RATIONALE.MT_1018" />
        <div>
          <ExternalLink
            href="https://learn.microsoft.com/en-us/entra/identity/conditional-access/howto-policy-persistent-browser-session"
            label="https://learn.microsoft.com/en-us/entra/identity/conditional-access/howto-policy-persistent-browser-session"
          />
        </div>
      </>
    ),
    compliance: {
      [COMPLIANCE_TYPES.CIS_31]: ["5.2.2.4"],
    },
    statusDetailsFormatter: (row) =>
      row.status === AUGMENTT_POSTURE_STATES.COMPLIANT ? (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.POSITIVE.MT_1018" />
      ) : (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.NEGATIVE.MT_1018" />
      ),
  },
  [SECURE_CHECK_TYPES.MT_1017]: {
    shortcut: SHORTCUTS.CAP,
    rationale: (
      <>
        <FormattedMessage id="SECURE.RATIONALE.MT_1017" />
        <div>
          <ExternalLink
            href="https://learn.microsoft.com/en-us/entra/identity/conditional-access/howto-policy-persistent-browser-session"
            label="https://learn.microsoft.com/en-us/entra/identity/conditional-access/howto-policy-persistent-browser-session"
          />
        </div>
      </>
    ),
    statusDetailsFormatter: (row) =>
      row.status === AUGMENTT_POSTURE_STATES.COMPLIANT ? (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.POSITIVE.MT_1017" />
      ) : (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.NEGATIVE.MT_1017" />
      ),
  },
  [SECURE_CHECK_TYPES.MT_1015]: {
    shortcut: SHORTCUTS.CAP,
    rationale: (
      <>
        <FormattedMessage id="SECURE.RATIONALE.MT_1015" />
        <div>
          <ExternalLink
            href="https://learn.microsoft.com/en-us/entra/identity/conditional-access/howto-policy-unknown-unsupported-device"
            label="https://learn.microsoft.com/en-us/entra/identity/conditional-access/howto-policy-unknown-unsupported-device"
          />
        </div>
      </>
    ),
    statusDetailsFormatter: (row) =>
      row.status === AUGMENTT_POSTURE_STATES.COMPLIANT ? (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.POSITIVE.MT_1015" />
      ) : (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.NEGATIVE.MT_1015" />
      ),
  },
  [SECURE_CHECK_TYPES.MT_1014]: {
    shortcut: SHORTCUTS.CAP,
    rationale: (
      <>
        <FormattedMessage id="SECURE.RATIONALE.MT_1014" />
        <div>
          <ExternalLink
            href="https://learn.microsoft.com/en-us/entra/identity/conditional-access/howto-conditional-access-policy-compliant-device-admin"
            label="https://learn.microsoft.com/en-us/entra/identity/conditional-access/howto-conditional-access-policy-compliant-device-admin"
          />
        </div>
      </>
    ),
    statusDetailsFormatter: (row) =>
      row.status === AUGMENTT_POSTURE_STATES.COMPLIANT ? (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.POSITIVE.MT_1014" />
      ) : (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.NEGATIVE.MT_1014" />
      ),
  },
  [SECURE_CHECK_TYPES.MT_1013]: {
    rationale: "SECURE.RATIONALE.MT_1013",
    shortcut: SHORTCUTS.CAP,
    statusDetailsFormatter: (row) =>
      row.status === AUGMENTT_POSTURE_STATES.COMPLIANT ? (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.POSITIVE.MT_1013" />
      ) : (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.NEGATIVE.MT_1013" />
      ),
    compliance: {
      [COMPLIANCE_TYPES.CIS_31]: ["5.2.2.6"],
    },
  },
  [SECURE_CHECK_TYPES.MT_1011]: {
    shortcut: SHORTCUTS.CAP,
    rationale: (
      <>
        <FormattedMessage id="SECURE.RATIONALE.MT_1011" />
        <div>
          <ExternalLink
            href="https://learn.microsoft.com/en-us/entra/identity/conditional-access/howto-conditional-access-policy-registration"
            label="https://learn.microsoft.com/en-us/entra/identity/conditional-access/howto-conditional-access-policy-registration"
          />
        </div>
      </>
    ),
    statusDetailsFormatter: (row) =>
      row.status === AUGMENTT_POSTURE_STATES.COMPLIANT ? (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.POSITIVE.MT_1011" />
      ) : (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.NEGATIVE.MT_1011" />
      ),
  },
  [SECURE_CHECK_TYPES.MT_1005]: {
    rationale: "SECURE.RATIONALE.MT_1005",
    statusDetailsFormatter: (row) =>
      row.status === AUGMENTT_POSTURE_STATES.COMPLIANT ? (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.POSITIVE.MT_1005" />
      ) : (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.NEGATIVE.MT_1005" />
      ),
  },
  [SECURE_CHECK_TYPES.MT_1004]: {
    rationale: (
      <>
        <FormattedMessage id="SECURE.RATIONALE.MT_1004" />
        <div>
          <ExternalLink
            href="https://learn.microsoft.com/en-us/entra/identity/conditional-access/plan-conditional-access#apply-conditional-access-policies-to-every-app"
            label="https://learn.microsoft.com/en-us/entra/identity/conditional-access/plan-conditional-access#apply-conditional-access-policies-to-every-app"
          />
        </div>
      </>
    ),
    statusDetailsFormatter: (row) =>
      row.status === AUGMENTT_POSTURE_STATES.COMPLIANT ? (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.POSITIVE.MT_1004" />
      ) : (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.NEGATIVE.MT_1004" />
      ),
  },
  [SECURE_CHECK_TYPES.MT_1003]: {
    rationale: (
      <>
        <FormattedMessage id="SECURE.RATIONALE.MT_1003" />
        <div>
          <ExternalLink
            href="https://learn.microsoft.com/en-us/entra/identity/conditional-access/plan-conditional-access#apply-conditional-access-policies-to-every-app"
            label="https://learn.microsoft.com/en-us/entra/identity/conditional-access/plan-conditional-access#apply-conditional-access-policies-to-every-app"
          />
        </div>
      </>
    ),
    statusDetailsFormatter: (row) =>
      row.status === AUGMENTT_POSTURE_STATES.COMPLIANT ? (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.POSITIVE.MT_1003" />
      ) : (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.NEGATIVE.MT_1003" />
      ),
  },
  [SECURE_CHECK_TYPES.MT_1002]: {
    rationale: (
      <>
        <FormattedMessage id="SECURE.RATIONALE.MT_1002" />
        <div>
          <ExternalLink
            href="https://learn.microsoft.com/en-us/graph/api/resources/tenantappmanagementpolicy?view=graph-rest-1.0"
            label="https://learn.microsoft.com/en-us/graph/api/resources/tenantappmanagementpolicy?view=graph-rest-1.0"
          />
        </div>
      </>
    ),
    statusDetailsFormatter: (row) =>
      row.status === AUGMENTT_POSTURE_STATES.COMPLIANT ? (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.POSITIVE.MT_1002" />
      ) : (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.NEGATIVE.MT_1002" />
      ),
  },
  [SECURE_CHECK_TYPES.MT_1001]: {
    shortcut: SHORTCUTS.CAP,
    rationale: (
      <>
        <FormattedMessage id="SECURE.RATIONALE.MT_1001" />
        <div>
          <ExternalLink
            href="https://learn.microsoft.com/en-us/entra/identity/conditional-access/howto-conditional-access-policy-compliant-device"
            label="https://learn.microsoft.com/en-us/entra/identity/conditional-access/howto-conditional-access-policy-compliant-device"
          />
        </div>
      </>
    ),
    statusDetailsFormatter: (row) =>
      row.status === AUGMENTT_POSTURE_STATES.COMPLIANT ? (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.POSITIVE.MT_1001" />
      ) : (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.NEGATIVE.MT_1001" />
      ),
  },
  [SECURE_CHECK_TYPES.MS_EXO_7_1]: {
    compliance: { [COMPLIANCE_TYPES.CISA_SCUBA]: ["MS.EXO.7.1"] },
    rationale: "SECURE.RATIONALE.MS_EXO_7_1",
    statusDetailsFormatter: (row) =>
      row.status === AUGMENTT_POSTURE_STATES.COMPLIANT ? (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.POSITIVE.MS_EXO_7_1" />
      ) : (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.NEGATIVE.MS_EXO_7_1" />
      ),
  },
  [SECURE_CHECK_TYPES.MS_EXO_6_2]: {
    compliance: {
      [COMPLIANCE_TYPES.CIS_31]: ["1.3.3"],
      [COMPLIANCE_TYPES.CISA_SCUBA]: ["MS.EXO.6.2"],
    },
    rationale: "SECURE.RATIONALE.MS_EXO_6_2",
    statusDetailsFormatter: (row) =>
      row.status === AUGMENTT_POSTURE_STATES.COMPLIANT ? (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.POSITIVE.MS_EXO_6_2" />
      ) : (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.NEGATIVE.MS_EXO_6_2" />
      ),
  },
  [SECURE_CHECK_TYPES.MS_EXO_6_1]: {
    compliance: { [COMPLIANCE_TYPES.CISA_SCUBA]: ["MS.EXO.6.1"] },
    rationale: "SECURE.RATIONALE.MS_EXO_6_1",
    statusDetailsFormatter: (row) =>
      row.status === AUGMENTT_POSTURE_STATES.COMPLIANT ? (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.POSITIVE.MS_EXO_6_1" />
      ) : (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.NEGATIVE.MS_EXO_6_1" />
      ),
  },
  [SECURE_CHECK_TYPES.MS_EXO_5_1]: {
    compliance: { [COMPLIANCE_TYPES.CISA_SCUBA]: ["MS.EXO.5.1"] },
    rationale: "SECURE.RATIONALE.MS_EXO_5_1",
    statusDetailsFormatter: (row) =>
      row.status === AUGMENTT_POSTURE_STATES.COMPLIANT ? (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.POSITIVE.MS_EXO_5_1" />
      ) : (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.NEGATIVE.MS_EXO_5_1" />
      ),
  },
  [SECURE_CHECK_TYPES.MS_EXO_4_3]: {
    compliance: { [COMPLIANCE_TYPES.CISA_SCUBA]: ["MS.EXO.4.3"] },
    rationale: "SECURE.RATIONALE.MS_EXO_4_3",
    statusDetailsFormatter: (row) =>
      row.status === AUGMENTT_POSTURE_STATES.COMPLIANT ? (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.POSITIVE.MS_EXO_4_3" />
      ) : (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.NEGATIVE.MS_EXO_4_3" />
      ),
  },
  [SECURE_CHECK_TYPES.MS_EXO_4_2]: {
    compliance: { [COMPLIANCE_TYPES.CISA_SCUBA]: ["MS.EXO.4.2"] },
    rationale: "SECURE.RATIONALE.MS_EXO_4_2",
    statusDetailsFormatter: (row) =>
      row.status === AUGMENTT_POSTURE_STATES.COMPLIANT ? (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.POSITIVE.MS_EXO_4_2" />
      ) : (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.NEGATIVE.MS_EXO_4_2" />
      ),
  },
  [SECURE_CHECK_TYPES.MS_EXO_4_1]: {
    compliance: {
      [COMPLIANCE_TYPES.CIS_31]: ["2.1.10"],
      [COMPLIANCE_TYPES.CISA_SCUBA]: ["MS.EXO.4.1"],
    },
    rationale: "SECURE.RATIONALE.MS_EXO_4_1",
    statusDetailsFormatter: (row) =>
      row.status === AUGMENTT_POSTURE_STATES.COMPLIANT ? (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.POSITIVE.MS_EXO_4_1" />
      ) : (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.NEGATIVE.MS_EXO_4_1" />
      ),
  },
  [SECURE_CHECK_TYPES.MS_EXO_2_2]: {
    compliance: {
      [COMPLIANCE_TYPES.CIS_31]: ["2.1.8"],
      [COMPLIANCE_TYPES.CISA_SCUBA]: ["MS.EXO.2.2"],
    },
    rationale: "SECURE.RATIONALE.MS_EXO_2_2",
    statusDetailsFormatter: (row) =>
      row.status === AUGMENTT_POSTURE_STATES.COMPLIANT ? (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.POSITIVE.MS_EXO_2_2" />
      ) : (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.NEGATIVE.MS_EXO_2_2" />
      ),
  },
  [SECURE_CHECK_TYPES.MS_EXO_2_1]: {
    compliance: { [COMPLIANCE_TYPES.CISA_SCUBA]: ["MS.EXO.2.1"] },
    rationale: "SECURE.RATIONALE.MS_EXO_2_1",
    statusDetailsFormatter: (row) =>
      row.status === AUGMENTT_POSTURE_STATES.COMPLIANT ? (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.POSITIVE.MS_EXO_2_1" />
      ) : (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.NEGATIVE.MS_EXO_2_1" />
      ),
  },
  [SECURE_CHECK_TYPES.MS_AAD_8_2]: {
    compliance: { [COMPLIANCE_TYPES.CISA_SCUBA]: ["MS.AAD.8.2"] },
    rationale: "SECURE.RATIONALE.MS_AAD_8_2",
    statusDetailsFormatter: (row) =>
      row.status === AUGMENTT_POSTURE_STATES.COMPLIANT ? (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.POSITIVE.MS_AAD_8_2" />
      ) : (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.NEGATIVE.MS_AAD_8_2" />
      ),
  },
  [SECURE_CHECK_TYPES.MS_AAD_8_1]: {
    compliance: { [COMPLIANCE_TYPES.CISA_SCUBA]: ["MS.AAD.8.1"] },
    rationale: "SECURE.RATIONALE.MS_AAD_8_1",
    statusDetailsFormatter: (row) =>
      row.status === AUGMENTT_POSTURE_STATES.COMPLIANT ? (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.POSITIVE.MS_AAD_8_1" />
      ) : (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.NEGATIVE.MS_AAD_8_1" />
      ),
  },
  [SECURE_CHECK_TYPES.MS_AAD_7_1]: {
    rationale: "SECURE.RATIONALE.MS_AAD_7_1",
    statusDetailsFormatter: (row) =>
      row.status === AUGMENTT_POSTURE_STATES.COMPLIANT ? (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.POSITIVE.MS_AAD_7_1" />
      ) : (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.NEGATIVE.MS_AAD_7_1" />
      ),
    compliance: { [COMPLIANCE_TYPES.CISA_SCUBA]: ["MS.AAD.7.1"] },
  },
  [SECURE_CHECK_TYPES.MS_AAD_6_1]: {
    rationale: "SECURE.RATIONALE.MS_AAD_6_1",
    statusDetailsFormatter: (row) =>
      row.status === AUGMENTT_POSTURE_STATES.COMPLIANT ? (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.POSITIVE.MS_AAD_6_1" />
      ) : (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.NEGATIVE.MS_AAD_6_1" />
      ),
    compliance: {
      [COMPLIANCE_TYPES.CIS_31]: ["1.3.1"],
      [COMPLIANCE_TYPES.CISA_SCUBA]: ["MS.AAD.6.1"],
    },
  },
  [SECURE_CHECK_TYPES.MS_AAD_5_3]: {
    rationale: "SECURE.RATIONALE.MS_AAD_5_3",
    statusDetailsFormatter: (row) =>
      row.status === AUGMENTT_POSTURE_STATES.COMPLIANT ? (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.POSITIVE.MS_AAD_5_3" />
      ) : (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.NEGATIVE.MS_AAD_5_3" />
      ),
    compliance: {
      [COMPLIANCE_TYPES.CIS_31]: ["5.1.5.3"],
      [COMPLIANCE_TYPES.CISA_SCUBA]: ["MS.AAD.5.3"],
    },
  },
  [SECURE_CHECK_TYPES.MS_AAD_5_1]: {
    rationale: "SECURE.RATIONALE.MS_AAD_5_1",
    statusDetailsFormatter: (row) =>
      row.status === AUGMENTT_POSTURE_STATES.COMPLIANT ? (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.POSITIVE.MS_AAD_5_1" />
      ) : (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.NEGATIVE.MS_AAD_5_1" />
      ),
    compliance: { [COMPLIANCE_TYPES.CISA_SCUBA]: ["MS.AAD.5.1"] },
  },
  [SECURE_CHECK_TYPES.MS_AAD_3_8]: {
    rationale: "SECURE.RATIONALE.MS_AAD_3_8",
    shortcut: SHORTCUTS.CAP,
    statusDetailsFormatter: (row) =>
      row.status === AUGMENTT_POSTURE_STATES.COMPLIANT ? (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.POSITIVE.MS_AAD_3_8" />
      ) : (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.NEGATIVE.MS_AAD_3_8" />
      ),
    compliance: { [COMPLIANCE_TYPES.CISA_SCUBA]: ["MS.AAD.3.8"] },
  },
  [SECURE_CHECK_TYPES.MS_AAD_3_7]: {
    rationale: "SECURE.RATIONALE.MS_AAD_3_7",
    statusDetailsFormatter: (row) =>
      row.status === AUGMENTT_POSTURE_STATES.COMPLIANT ? (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.POSITIVE.MS_AAD_3_7" />
      ) : (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.NEGATIVE.MS_AAD_3_7" />
      ),
    compliance: { [COMPLIANCE_TYPES.CISA_SCUBA]: ["MS.AAD.3.7"] },
  },
  [SECURE_CHECK_TYPES.MS_AAD_3_6]: {
    rationale: "SECURE.RATIONALE.MS_AAD_3_6",
    shortcut: SHORTCUTS.CAP,
    statusDetailsFormatter: (row) =>
      row.status === AUGMENTT_POSTURE_STATES.COMPLIANT ? (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.POSITIVE.MS_AAD_3_6" />
      ) : (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.NEGATIVE.MS_AAD_3_6" />
      ),
    compliance: {
      [COMPLIANCE_TYPES.CISA_SCUBA]: ["MS.AAD.3.6"],
      [COMPLIANCE_TYPES.ESSENTIAL_8_L2]: ["1682"],
      [COMPLIANCE_TYPES.ESSENTIAL_8_L3]: ["1682"],
    },
  },
  [SECURE_CHECK_TYPES.MS_AAD_3_5]: {
    rationale: "SECURE.RATIONALE.MS_AAD_3_5",
    statusDetailsFormatter: (row) =>
      row.status === AUGMENTT_POSTURE_STATES.COMPLIANT ? (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.POSITIVE.MS_AAD_3_5" />
      ) : (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.NEGATIVE.MS_AAD_3_5" />
      ),
    compliance: { [COMPLIANCE_TYPES.CISA_SCUBA]: ["MS.AAD.3.5"] },
  },
  [SECURE_CHECK_TYPES.MS_AAD_3_3]: {
    rationale: "SECURE.RATIONALE.MS_AAD_3_3",
    statusDetailsFormatter: (row) =>
      row.status === AUGMENTT_POSTURE_STATES.COMPLIANT ? (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.POSITIVE.MS_AAD_3_3" />
      ) : (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.NEGATIVE.MS_AAD_3_3" />
      ),
    compliance: { [COMPLIANCE_TYPES.CISA_SCUBA]: ["MS.AAD.3.3"] },
  },
  [SECURE_CHECK_TYPES.MS_AAD_3_1]: {
    rationale: "SECURE.RATIONALE.MS_AAD_3_1",
    statusDetailsFormatter: (row) =>
      row.status === AUGMENTT_POSTURE_STATES.COMPLIANT ? (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.POSITIVE.MS_AAD_3_1" />
      ) : (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.NEGATIVE.MS_AAD_3_1" />
      ),
    compliance: {
      [COMPLIANCE_TYPES.CIS_31]: ["5.2.2.5"],
      [COMPLIANCE_TYPES.CISA_SCUBA]: ["MS.AAD.3.1"],
      [COMPLIANCE_TYPES.ESSENTIAL_8_L2]: ["1872", "1682", "1894"],
      [COMPLIANCE_TYPES.ESSENTIAL_8_L3]: ["1872", "1682", "1894", "1874"],
    },
  },
  [SECURE_CHECK_TYPES.MS_AAD_2_3]: {
    rationale: "SECURE.RATIONALE.MS_AAD_2_3",
    compliance: { [COMPLIANCE_TYPES.CISA_SCUBA]: ["MS.AAD.2.3"] },
    shortcut: SHORTCUTS.CAP,
  },
  [SECURE_CHECK_TYPES.MS_AAD_2_1]: {
    compliance: { [COMPLIANCE_TYPES.CISA_SCUBA]: ["MS.AAD.2.1"] },
    rationale: "SECURE.RATIONALE.MS_AAD_2_1",
    shortcut: SHORTCUTS.CAP,
    statusDetailsFormatter: (row) =>
      row.status === AUGMENTT_POSTURE_STATES.COMPLIANT ? (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.POSITIVE.MS_AAD_2_1" />
      ) : (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.NEGATIVE.MS_AAD_2_1" />
      ),
  },
  [SECURE_CHECK_TYPES.MS_EXO_12_2]: {
    rationale: "SECURE.RATIONALE.MS_EXO_12_2",
    statusDetailsFormatter: (row) =>
      row.status === AUGMENTT_POSTURE_STATES.COMPLIANT ? (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.POSITIVE.MS_EXO_12_2" />
      ) : (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.NEGATIVE.MS_EXO_12_2" />
      ),
    compliance: {
      [COMPLIANCE_TYPES.CIS_31]: ["6.2.2"],
      [COMPLIANCE_TYPES.CISA_SCUBA]: ["MS.EXO.12.2"],
    },
  },
  [SECURE_CHECK_TYPES.MS_EXO_12_1]: {
    rationale: "SECURE.RATIONALE.MS_EXO_12_1",
    statusDetailsFormatter: (row) =>
      row.status === AUGMENTT_POSTURE_STATES.COMPLIANT ? (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.POSITIVE.MS_EXO_12_1" />
      ) : (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.NEGATIVE.MS_EXO_12_1" />
      ),
    compliance: {
      [COMPLIANCE_TYPES.CISA_SCUBA]: ["MS.EXO.12.1"],
    },
  },
  [SECURE_CHECK_TYPES.MS_AAD_7_6]: {
    rationale: "SECURE.RATIONALE.MS_AAD_7_6",
    statusDetailsFormatter: (row) =>
      row.status === AUGMENTT_POSTURE_STATES.COMPLIANT ? (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.POSITIVE.MS_AAD_7_6" />
      ) : (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.NEGATIVE.MS_AAD_7_6" />
      ),
    compliance: {
      [COMPLIANCE_TYPES.CISA_SCUBA]: ["MS.AAD.7.6"],
    },
  },
  [SECURE_CHECK_TYPES.MS_AAD_7_5]: {
    rationale: "SECURE.RATIONALE.MS_AAD_7_5",
    statusDetailsFormatter: (row) =>
      row.status === AUGMENTT_POSTURE_STATES.COMPLIANT ? (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.POSITIVE.MS_AAD_7_5" />
      ) : (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.NEGATIVE.MS_AAD_7_5" />
      ),
    compliance: {
      [COMPLIANCE_TYPES.CISA_SCUBA]: ["MS.AAD.7.5"],
    },
  },
  [SECURE_CHECK_TYPES.MS_AAD_7_4]: {
    rationale: "SECURE.RATIONALE.MS_AAD_7_4",
    statusDetailsFormatter: (row) =>
      row.status === AUGMENTT_POSTURE_STATES.COMPLIANT ? (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.POSITIVE.MS_AAD_7_4" />
      ) : (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.NEGATIVE.MS_AAD_7_4" />
      ),
    compliance: {
      [COMPLIANCE_TYPES.CIS_31]: ["5.3.1"],
      [COMPLIANCE_TYPES.CISA_SCUBA]: ["MS.AAD.7.4"],
    },
  },
  [SECURE_CHECK_TYPES.MS_AAD_7_3]: {
    rationale: "SECURE.RATIONALE.MS_AAD_7_3",
    statusDetailsFormatter: (row) =>
      row.status === AUGMENTT_POSTURE_STATES.COMPLIANT ? (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.POSITIVE.MS_AAD_7_3" />
      ) : (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.NEGATIVE.MS_AAD_7_3" />
      ),
    compliance: {
      [COMPLIANCE_TYPES.CIS_31]: ["1.1.1"],
      [COMPLIANCE_TYPES.CISA_SCUBA]: ["MS.AAD.7.3"],
    },
  },
  [SECURE_CHECK_TYPES.EIDSCA_PR02]: {
    rationale: (
      <>
        <FormattedMessage id="SECURE.RATIONALE.EIDSCA_PR02" />
        <div>
          <ExternalLink
            href="https://learn.microsoft.com/en-us/azure/security/fundamentals/identity-management-best-practices#enable-password-management"
            label="https://learn.microsoft.com/en-us/azure/security/fundamentals/identity-management-best-practices#enable-password-management"
          />
        </div>
      </>
    ),
    statusDetailsFormatter: (row) =>
      row.status === AUGMENTT_POSTURE_STATES.COMPLIANT ? (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.POSITIVE.EIDSCA_PR02" />
      ) : (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.NEGATIVE.EIDSCA_PR02" />
      ),
  },
  [SECURE_CHECK_TYPES.EIDSCA_PR01]: {
    rationale: (
      <>
        <FormattedMessage id="SECURE.RATIONALE.EIDSCA_PR01" />
        <div>
          <ExternalLink
            href="https://learn.microsoft.com/en-us/entra/identity/authentication/concept-password-ban-bad-on-premises"
            label="https://learn.microsoft.com/en-us/entra/identity/authentication/concept-password-ban-bad-on-premises"
          />
        </div>
      </>
    ),
    statusDetailsFormatter: (row) =>
      row.status === AUGMENTT_POSTURE_STATES.COMPLIANT ? (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.POSITIVE.EIDSCA_PR01" />
      ) : (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.NEGATIVE.EIDSCA_PR01" />
      ),
  },
  [SECURE_CHECK_TYPES.EIDSCA_CR04]: {
    rationale: "SECURE.RATIONALE.EIDSCA_CR04",
    statusDetailsFormatter: (row) =>
      row.status === AUGMENTT_POSTURE_STATES.COMPLIANT ? (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.POSITIVE.EIDSCA_CR04" />
      ) : (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.NEGATIVE.EIDSCA_CR04" />
      ),
  },
  [SECURE_CHECK_TYPES.EIDSCA_CR03]: {
    rationale: "SECURE.RATIONALE.EIDSCA_CR03",
    statusDetailsFormatter: (row) =>
      row.status === AUGMENTT_POSTURE_STATES.COMPLIANT ? (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.POSITIVE.EIDSCA_CR03" />
      ) : (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.NEGATIVE.EIDSCA_CR03" />
      ),
  },
  [SECURE_CHECK_TYPES.EIDSCA_CR02]: {
    rationale: "SECURE.RATIONALE.EIDSCA_CR02",
    statusDetailsFormatter: (row) =>
      row.status === AUGMENTT_POSTURE_STATES.COMPLIANT ? (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.POSITIVE.EIDSCA_CR02" />
      ) : (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.NEGATIVE.EIDSCA_CR02" />
      ),
  },
  [SECURE_CHECK_TYPES.MS_AAD_7_2]: {
    rationale: "SECURE.RATIONALE.MS_AAD_7_2",
    statusDetailsFormatter: (row) =>
      row.status === AUGMENTT_POSTURE_STATES.COMPLIANT ? (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.POSITIVE.MS_AAD_7_2" />
      ) : (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.NEGATIVE.MS_AAD_7_2" />
      ),
    compliance: {
      [COMPLIANCE_TYPES.CISA_SCUBA]: ["MS.AAD.7.2"],
    },
  },
  [SECURE_CHECK_TYPES.EIDSCA_AM09]: {
    rationale: "SECURE.RATIONALE.EIDSCA_AM09",
    statusDetailsFormatter: (row) =>
      row.status === AUGMENTT_POSTURE_STATES.COMPLIANT ? (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.POSITIVE.EIDSCA_AM09" />
      ) : (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.NEGATIVE.EIDSCA_AM09" />
      ),
    compliance: {
      [COMPLIANCE_TYPES.CIS_31]: ["5.2.3.1"],
    },
  },
  [SECURE_CHECK_TYPES.EIDSCA_AM06]: {
    rationale: "SECURE.RATIONALE.EIDSCA_AM06",
    statusDetailsFormatter: (row) =>
      row.status === AUGMENTT_POSTURE_STATES.COMPLIANT ? (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.POSITIVE.EIDSCA_AM06" />
      ) : (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.NEGATIVE.EIDSCA_AM06" />
      ),
    compliance: {
      [COMPLIANCE_TYPES.CIS_31]: ["5.2.3.1"],
    },
  },
  [SECURE_CHECK_TYPES.EIDSCA_AG02]: {
    rationale: "SECURE.RATIONALE.EIDSCA_AG02",
    statusDetailsFormatter: (row) =>
      row.status === AUGMENTT_POSTURE_STATES.COMPLIANT ? (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.POSITIVE.EIDSCA_AG02" />
      ) : (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.NEGATIVE.EIDSCA_AG02" />
      ),
  },
  [SECURE_CHECK_TYPES.EIDSCA_AF02]: {
    rationale: "SECURE.RATIONALE.EIDSCA_AF02",
    statusDetailsFormatter: (row) =>
      row.status === AUGMENTT_POSTURE_STATES.COMPLIANT ? (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.POSITIVE.EIDSCA_AF02" />
      ) : (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.NEGATIVE.EIDSCA_AF02" />
      ),
  },
  [SECURE_CHECK_TYPES.EIDSCA_AF01]: {
    rationale: "SECURE.RATIONALE.EIDSCA_AF01",
    statusDetailsFormatter: (row) =>
      row.status === AUGMENTT_POSTURE_STATES.COMPLIANT ? (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.POSITIVE.EIDSCA_AF01" />
      ) : (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.NEGATIVE.EIDSCA_AF01" />
      ),
  },
  [SECURE_CHECK_TYPES.MT_1023]: {
    rationale: "SECURE.RATIONALE.MT_1023",
    statusDetailsFormatter: (row) =>
      row.status === AUGMENTT_POSTURE_STATES.COMPLIANT ? (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.POSITIVE.MT_1023" />
      ) : (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.NEGATIVE.MT_1023" />
      ),
  },
  [SECURE_CHECK_TYPES.MT_1022]: {
    rationale: "SECURE.RATIONALE.MT_1022",
    statusDetailsFormatter: (row) =>
      row.status === AUGMENTT_POSTURE_STATES.COMPLIANT ? (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.POSITIVE.MT_1022" />
      ) : (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.NEGATIVE.MT_1022" />
      ),
  },
  [SECURE_CHECK_TYPES.EIDSCA_PR06]: {
    rationale: (
      <>
        <FormattedMessage id="SECURE.RATIONALE.EIDSCA_PR06" />
        <div>
          <ExternalLink
            href="https://learn.microsoft.com/en-us/entra/identity/authentication/howto-password-smart-lockout"
            label="https://learn.microsoft.com/en-us/entra/identity/authentication/howto-password-smart-lockout"
          />
        </div>
      </>
    ),
    statusDetailsFormatter: (row) =>
      row.status === AUGMENTT_POSTURE_STATES.COMPLIANT ? (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.POSITIVE.EIDSCA_PR06" />
      ) : (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.NEGATIVE.EIDSCA_PR06" />
      ),
  },
  [SECURE_CHECK_TYPES.EIDSCA_PR05]: {
    rationale: (
      <>
        <FormattedMessage id="SECURE.RATIONALE.EIDSCA_PR05" />
        <div>
          <ExternalLink
            href="https://learn.microsoft.com/en-us/entra/identity/authentication/howto-password-smart-lockout"
            label="https://learn.microsoft.com/en-us/entra/identity/authentication/howto-password-smart-lockout"
          />
        </div>
      </>
    ),
    statusDetailsFormatter: (row) =>
      row.status === AUGMENTT_POSTURE_STATES.COMPLIANT ? (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.POSITIVE.EIDSCA_PR05" />
      ) : (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.NEGATIVE.EIDSCA_PR05" />
      ),
  },
  [SECURE_CHECK_TYPES.EIDSCA_PR03]: {
    rationale: "SECURE.RATIONALE.EIDSCA_PR03",
    compliance: {
      [COMPLIANCE_TYPES.CIS_31]: ["5.2.3.2"],
    },
    statusDetailsFormatter: (row) =>
      row.status === AUGMENTT_POSTURE_STATES.COMPLIANT ? (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.POSITIVE.EIDSCA_PR03" />
      ) : (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.NEGATIVE.EIDSCA_PR03" />
      ),
  },
  [SECURE_CHECK_TYPES.EIDSCA_CP03]: {
    rationale: (
      <>
        <FormattedMessage id="SECURE.RATIONALE.EIDSCA_CP03" />
        <div>
          <ExternalLink
            href="https://learn.microsoft.com/en-us/entra/identity/enterprise-apps/configure-risk-based-step-up-consent"
            label="https://learn.microsoft.com/en-us/entra/identity/enterprise-apps/configure-risk-based-step-up-consent"
          />
        </div>
      </>
    ),
    statusDetailsFormatter: (row) =>
      row.status === AUGMENTT_POSTURE_STATES.COMPLIANT ? (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.POSITIVE.EIDSCA_CP03" />
      ) : (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.NEGATIVE.EIDSCA_CP03" />
      ),
  },
  [SECURE_CHECK_TYPES.EIDSCA_AV01]: {
    rationale: "SECURE.RATIONALE.EIDSCA_AV01",
    statusDetailsFormatter: (row) =>
      row.status === AUGMENTT_POSTURE_STATES.COMPLIANT ? (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.POSITIVE.EIDSCA_AV01" />
      ) : (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.NEGATIVE.EIDSCA_AV01" />
      ),
  },
  [SECURE_CHECK_TYPES.EIDSCA_AT02]: {
    rationale: "SECURE.RATIONALE.EIDSCA_AT02",
    statusDetailsFormatter: (row) =>
      row.status === AUGMENTT_POSTURE_STATES.COMPLIANT ? (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.POSITIVE.EIDSCA_AT02" />
      ) : (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.NEGATIVE.EIDSCA_AT02" />
      ),
  },
  [SECURE_CHECK_TYPES.EIDSCA_AP14]: {
    rationale: "SECURE.RATIONALE.EIDSCA_AP14",
    statusDetailsFormatter: (row) =>
      row.status === AUGMENTT_POSTURE_STATES.COMPLIANT ? (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.POSITIVE.EIDSCA_AP14" />
      ) : (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.NEGATIVE.EIDSCA_AP14" />
      ),
  },
  [SECURE_CHECK_TYPES.EIDSCA_AP10]: {
    rationale: "SECURE.RATIONALE.EIDSCA_AP10",
    statusDetailsFormatter: (row) =>
      row.status === AUGMENTT_POSTURE_STATES.COMPLIANT ? (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.POSITIVE.EIDSCA_AP10" />
      ) : (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.NEGATIVE.EIDSCA_AP10" />
      ),
    compliance: {
      [COMPLIANCE_TYPES.CISA_SCUBA]: ["MS.AAD.2.6"],
    },
  },
  [SECURE_CHECK_TYPES.EIDSCA_AP09]: {
    rationale: (
      <>
        <FormattedMessage id="SECURE.RATIONALE.EIDSCA_AP09" />
        <div>
          <ExternalLink
            href="https://learn.microsoft.com/en-us/entra/identity/enterprise-apps/configure-risk-based-step-up-consent"
            label="https://learn.microsoft.com/en-us/entra/identity/enterprise-apps/configure-risk-based-step-up-consent"
          />
        </div>
      </>
    ),
    statusDetailsFormatter: (row) =>
      row.status === AUGMENTT_POSTURE_STATES.COMPLIANT ? (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.POSITIVE.EIDSCA_AP09" />
      ) : (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.NEGATIVE.EIDSCA_AP09" />
      ),
  },
  [SECURE_CHECK_TYPES.EIDSCA_AP08]: {
    rationale: "SECURE.RATIONALE.EIDSCA_AP08",
    statusDetailsFormatter: (row) =>
      row.status === AUGMENTT_POSTURE_STATES.COMPLIANT ? (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.POSITIVE.EIDSCA_AP08" />
      ) : (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.NEGATIVE.EIDSCA_AP08" />
      ),
    compliance: {
      [COMPLIANCE_TYPES.CIS_31]: ["5.1.2.2", "5.1.5.2"],
      [COMPLIANCE_TYPES.CISA_SCUBA]: ["MS.AAD.2.7"],
    },
  },
  [SECURE_CHECK_TYPES.EIDSCA_AP07]: {
    rationale: "SECURE.RATIONALE.EIDSCA_AP07",
    statusDetailsFormatter: (row) =>
      row.status === AUGMENTT_POSTURE_STATES.COMPLIANT ? (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.POSITIVE.EIDSCA_AP07" />
      ) : (
        <FormattedMessage id="SECURE.STATUS_DESCRIPTION.NEGATIVE.EIDSCA_AP07" />
      ),
    compliance: {
      [COMPLIANCE_TYPES.CISA_SCUBA]: ["MS.AAD.2.18"],
    },
  },
  ...secureScoreProfilePostureConfigs({
    AUGMENTT_POSTURE_STATES,
    COMPLIANCE_TYPES,
    SHORTCUTS,
  }),
};

export default postureConfig;
